import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { Toast, ConfirmationModal } from '../components/ui/Notification';
import { 
  FiArrowLeft, FiSave, FiCopy, FiCheck, FiSend, FiFileText, 
  FiGlobe, FiCalendar, FiMail, FiLock, FiLoader, FiInfo, 
  FiExternalLink, FiActivity, FiShield, FiCheckCircle, FiAlertTriangle
} from 'react-icons/fi';

// Date formatting helper
function formatDisplayDate(value) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = String(date.getDate()).padStart(2, "0");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const weekdays = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  return `${day}-${months[date.getMonth()]}-${date.getFullYear()}, ${weekdays[date.getDay()]}`;
}

export default function AdminClientDetail() {
  const { clientKey } = useParams();
  const navigate = useNavigate();
  const { adminUser } = useAuth();
  
  const [loading, setLoading] = useState(!!clientKey);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notification, setNotification] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [testStage, setTestStage] = useState(0);
  const [saveStage, setSaveStage] = useState(0);
  const [sheetStage, setSheetStage] = useState(0);
  const [testResult, setTestResult] = useState(null);
  const [modal, setModal] = useState({ open: false, type: null });

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.split('T')[0];
  };

  const [formData, setFormData] = useState({
    client_key: '',
    client_name: '',
    business_name: '',
    website_domain: '',
    allowed_domains: '',
    status: 'Trial',
    plan: 'Standard',
    notes: '',
    service_start_date: formatDateForInput(new Date().toISOString()),
    service_end_date: '',
    grace_period_days: 7,
    disable_notifications_after_end_date: true,
    send_business_notification: true,
    business_notification_email: '',
    cc_email: '',
    reply_to_email: '',
    send_customer_confirmation: false,
    customer_ack_from_name: '',
    customer_confirmation_subject: 'Thank you for your enquiry',
    send_sms_alert: false,
    sms_mobile: '',
    client_portal_enabled: true,
    client_login_emails: '',
    client_login_code: '',
    client_portal_role: 'Client Lead',
    sheet_id: '',
    sheet_url: '',
    client_sheet_auto_write: true
  });

  const SCRIPT_BASE = import.meta.env.VITE_BPAS_PUBLIC_SCRIPT_BASE_URL;

  const scriptInfo = useMemo(() => {
    const key = formData.client_key || clientKey;
    const isSaved = !!key;
    return {
      code: isSaved && SCRIPT_BASE ? `<script async src="${SCRIPT_BASE}?k=${key}"></script>` : null,
      isSaved
    };
  }, [formData.client_key, clientKey, SCRIPT_BASE]);

  useEffect(() => {
    if (clientKey) {
      const fetchClient = async () => {
        try {
          const res = await adminApi.getClient(adminUser.admin_session_token, clientKey);
          if (res.success && res.client) {
            setFormData(prev => ({ 
              ...prev, 
              ...res.client,
              service_start_date: formatDateForInput(res.client.service_start_date),
              service_end_date: formatDateForInput(res.client.service_end_date)
            }));
          }
        } catch (e) {
          setNotification({ message: 'Failed to load client data.', type: 'error' });
        } finally {
          setLoading(false);
        }
      };
      fetchClient();
    }
  }, [clientKey, adminUser]);

  const validateForm = () => {
    const errors = { profile: [], notifications: [], portal: [] };
    if (!formData.client_name) errors.profile.push("Internal Client Name");
    if (!formData.business_name) errors.profile.push("Client Business Name");
    if (!formData.website_domain) errors.profile.push("Website Domain");
    if (formData.send_business_notification && !formData.business_notification_email) errors.notifications.push("Recipient Email (Business Alerts)");
    if (formData.send_customer_confirmation && !formData.reply_to_email) errors.notifications.push("Reply-to Address (Customer Ack)");
    if (formData.client_portal_enabled) {
      if (!formData.client_login_emails) errors.portal.push("Client Login Email(s)");
      if (!formData.client_login_code || formData.client_login_code.length !== 6) errors.portal.push("Client Portal Passcode (6 digits)");
    }
    const hasErrors = errors.profile.length > 0 || errors.notifications.length > 0 || errors.portal.length > 0;
    setValidationErrors(hasErrors ? errors : {});
    return !hasErrors;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSaveStage(1);
    if (!validateForm()) {
      setSaveStage(0);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSaving(true);
    setSaveStage(2);
    try {
      setTimeout(() => setSaveStage(3), 500);
      const res = clientKey 
        ? await adminApi.updateClient(adminUser.admin_session_token, { ...formData, client_key: clientKey })
        : await adminApi.addClient(adminUser.admin_session_token, formData);
      setSaveStage(4);
      if (res.success) {
        setSaveStage(5);
        const updatedKey = res.client?.client_key || res.client_key;
        if (updatedKey) setFormData(prev => ({ ...prev, client_key: updatedKey }));
        setSaveStage(6);
        setNotification({ message: 'Configuration synchronized successfully.', type: 'success' });
        if (!clientKey && updatedKey) setTimeout(() => navigate(`/admin/clients/${updatedKey}`, { replace: true }), 1000);
      } else {
        setSaveStage(0);
        setNotification({ message: res.message || 'Synchronization failed.', type: 'error' });
      }
    } catch (e) {
      setSaveStage(0);
      setNotification({ message: 'Gateway error.', type: 'error' });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveStage(0), 3000);
    }
  };

  const handleSendTestLead = async () => {
    if (!clientKey) return setNotification({ message: 'Save client first.', type: 'error' });
    
    const testEmail = formData.business_notification_email || formData.reply_to_email;
    if (!testEmail) {
      return setNotification({ message: 'Add a business notification email before sending a test lead.', type: 'warning' });
    }

    setTestResult(null);
    setTestStage(1);
    try {
      setTimeout(() => setTestStage(2), 1000);
      
      const testPayload = {
        email: testEmail,
        name: 'BPAS Test Lead',
        phone: '0400 000 000',
        message: 'This is a BPAS gateway test submission.'
      };

      const res = await adminApi.sendTestLead(adminUser.admin_session_token, clientKey, testPayload);
      setTestStage(3);
      if (res.success) {
        setTestStage(4);
        setTimeout(() => setTestStage(5), 1000);
        setTestResult({ ...res, test_email: testEmail });
        setNotification({ message: 'Diagnostic lead created.', type: 'success' });
      } else {
        setNotification({ message: res.message || 'Test failed.', type: 'error' });
      }
    } catch (e) {
      setNotification({ message: 'Transmission failed.', type: 'error' });
    } finally {
      setTestStage(6);
    }
  };

  const handleCreateSheet = async () => {
    setSheetStage(1);
    try {
      setTimeout(() => setSheetStage(2), 1000);
      const res = await adminApi.createSheet(adminUser.admin_session_token, clientKey, formData.business_notification_email);
      setSheetStage(3);
      if (res.success) {
        setSheetStage(4);
        setFormData(prev => ({ ...prev, sheet_id: res.sheet_id, sheet_url: res.sheet_url }));
        setSheetStage(5);
        setNotification({ message: 'Vault sheet linked.', type: 'success' });
      } else {
        setSheetStage(0);
        setNotification({ message: 'Sheet creation failed.', type: 'error' });
      }
    } catch (e) {
      setSheetStage(0);
      setNotification({ message: 'Gateway error.', type: 'error' });
    } finally {
      setTimeout(() => setSheetStage(0), 3000);
    }
  };

  const getStatusText = (status, fallback) => {
    if (status === true || status === 'Yes') return 'Sent';
    if (status === false || status === 'No') return 'Failed';
    // Fallback parsing of notification_status string
    if (fallback && fallback.toLowerCase().includes('success')) return 'Sent';
    if (fallback && fallback.toLowerCase().includes('fail')) return 'Failed';
    return 'Pending';
  };

  if (loading) return <div className="flex items-center justify-center p-20"><SafeIcon icon={FiLoader} className="animate-spin text-3xl text-primary" /></div>;

  return (
    <div className="space-y-8 pb-20">
      {notification && <Toast {...notification} onClose={() => setNotification(null)} />}
      
      <ConfirmationModal 
        isOpen={modal.open && modal.type === 'createSheet'}
        title="Create Lead Sheet?"
        message="This will create a dedicated Google Sheet for this client's leads."
        confirmText="Create Sheet"
        loading={sheetStage > 0 && sheetStage < 5}
        onConfirm={() => { setModal({ open: false, type: null }); handleCreateSheet(); }}
        onCancel={() => setModal({ open: false, type: null })}
      />

      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/admin/clients')} className="w-10 h-10 bg-surface rounded-lg flex items-center justify-center shadow-soft text-muted hover:text-ink transition-premium border border-border">
            <SafeIcon icon={FiArrowLeft} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-ink tracking-tight">
              {clientKey ? (formData.business_name || 'Configuration') : 'New Client'}
            </h1>
            <p className="text-sm text-muted">
              {clientKey ? `ID: ${clientKey}` : 'Create new partner configuration'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 bg-surface p-2 rounded-lg border border-border">
            <button 
              type="button" 
              onClick={handleSendTestLead}
              disabled={testStage > 0 && testStage < 6}
              className="px-4 py-2 bg-slate-50 text-primary rounded-md text-sm font-medium flex items-center gap-2 hover:bg-primary hover:text-white transition-all disabled:opacity-50"
            >
              <SafeIcon icon={testStage > 0 && testStage < 6 ? FiLoader : FiSend} className={testStage > 0 && testStage < 6 ? 'animate-spin' : ''} />
              <span>Test</span>
            </button>
            <button 
              type="button" 
              onClick={() => setModal({ open: true, type: 'createSheet' })}
              disabled={!!formData.sheet_id || (sheetStage > 0 && sheetStage < 5)}
              className="px-4 py-2 bg-slate-50 text-success rounded-md text-sm font-medium flex items-center gap-2 hover:bg-success hover:text-white transition-all disabled:opacity-50"
            >
              <SafeIcon icon={(sheetStage > 0 && sheetStage < 5) ? FiLoader : FiFileText} className={(sheetStage > 0 && sheetStage < 5) ? 'animate-spin' : ''} />
              <span>Sheet</span>
            </button>
          </div>
          <button 
            type="submit" 
            form="clientForm"
            disabled={saving} 
            className="px-6 py-3 bg-gradient-to-r from-primary to-accent text-white rounded-xl font-medium flex items-center gap-2 shadow-card hover:-translate-y-0.5 transition-premium active:scale-95 disabled:opacity-50"
          >
            {saving ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <><SafeIcon icon={FiSave} /> {clientKey ? 'Update' : 'Create'}</>}
          </button>
        </div>
      </header>

      {clientKey && (
        <div className="bg-surface rounded-xl p-4 border border-border shadow-card flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-8">
            <div className="flex flex-col">
              <span className="text-xs text-muted">Status</span>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${formData.status === 'Active' ? 'bg-success' : 'bg-primary'} animate-pulse`} />
                <span className="text-sm font-medium text-ink">{formData.status}</span>
              </div>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-muted">Portal</span>
              <span className={`text-sm font-medium ${formData.client_portal_enabled ? 'text-success' : 'text-muted'}`}>
                {formData.client_portal_enabled ? 'Online' : 'Disabled'}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-muted">Sheet Sync</span>
              <span className={`text-sm font-medium ${formData.sheet_id ? 'text-success' : 'text-muted'}`}>
                {formData.sheet_id ? 'Active' : 'None'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {formData.sheet_url && (
              <a href={formData.sheet_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-primary hover:text-accent transition-colors">
                <SafeIcon icon={FiExternalLink} /> Lead Vault
              </a>
            )}
          </div>
        </div>
      )}

      {(saveStage > 0 || testStage > 0 || sheetStage > 0) && (
        <div className="bg-slate-900 text-white rounded-xl p-6 shadow-card flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-accent/20 rounded-lg flex items-center justify-center text-accent">
              <SafeIcon icon={FiLoader} className="animate-spin" />
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide">Processing</p>
              <p className="text-sm font-medium">
                {saveStage > 0 && ['', 'Validating...', 'Preparing...', 'Sending...', 'Saving...', 'Refreshing...', 'Complete'][saveStage]}
                {testStage > 0 && ['', 'Preparing lead...', 'Sending...', 'Waiting...', 'Creating...', 'Sending alerts...', 'Complete'][testStage]}
                {sheetStage > 0 && ['', 'Preparing...', 'Creating...', 'Linking...', 'Saving...', 'Complete'][sheetStage]}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className={`w-10 h-1 rounded-full ${
                (saveStage >= i || testStage >= i || (sheetStage >= i && i <= 5)) ? 'bg-accent' : 'bg-white/10'
              }`} />
            ))}
          </div>
        </div>
      )}

      {testResult && (
        <div className="bg-surface rounded-xl p-6 border border-border shadow-card">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-ink">Diagnostic Result</h4>
            <span className="px-3 py-1 bg-primary/5 text-primary rounded-md text-[10px] font-bold uppercase">
              Sent to: {testResult.test_email}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            {[
              { label: 'Lead Created', val: testResult.success ? 'Yes' : 'No', icon: FiCheckCircle },
              { label: 'Lead ID', val: testResult.lead_id?.slice(0,8) || 'N/A', icon: FiActivity },
              { label: 'Business Alert', val: getStatusText(testResult.business_email_sent, testResult.notification_status), icon: FiMail },
              { label: 'Customer Ack', val: getStatusText(testResult.customer_ack_sent, testResult.notification_status), icon: FiCheckCircle },
              { label: 'Gateway', val: 'Healthy', icon: FiShield }
            ].map(r => (
              <div key={r.label} className="bg-slate-50 p-4 rounded-lg border border-border">
                <div className="flex items-center gap-2 mb-1">
                  <SafeIcon icon={r.icon} className="text-slate-400 text-xs" />
                  <p className="text-[10px] text-muted uppercase tracking-wide font-bold">{r.label}</p>
                </div>
                <p className={`text-sm font-semibold ${r.val === 'Failed' ? 'text-error' : 'text-ink'}`}>{r.val}</p>
              </div>
            ))}
          </div>
          
          <div className="space-y-3">
            <div className="bg-primary/5 p-4 rounded-lg border border-primary/10 flex items-center gap-3">
              <SafeIcon icon={FiInfo} className="text-primary" />
              <p className="text-sm text-slate-600 leading-relaxed font-medium">
                Verify the Business Alert in <span className="text-ink font-bold">{testResult.test_email}</span>. 
                {formData.send_customer_confirmation && " Customer acknowledgement was also sent to this address for testing."}
              </p>
            </div>
            
            {formData.send_customer_confirmation && testResult.customer_ack_sent === 'No' && (
              <div className="bg-warning/5 p-4 rounded-lg border border-warning/20 flex items-center gap-3">
                <SafeIcon icon={FiAlertTriangle} className="text-warning" />
                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                  Customer acknowledgement is enabled but was not confirmed. Check the visitor email used for the test and Emailit logs.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <form id="clientForm" onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-8">
          <div className="bg-surface rounded-xl p-6 border border-border shadow-card">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-primary/5 rounded-lg flex items-center justify-center text-primary">
                  <SafeIcon icon={FiGlobe} />
                </div>
                <h3 className="text-lg font-semibold text-ink">Partner Profile</h3>
              </div>
              <p className="text-sm text-muted">Core business identity and domain authorization</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Internal Name <span className="text-error">*</span></label>
                <input type="text" value={formData.client_name} onChange={e => setFormData({...formData, client_name: e.target.value})} placeholder="e.g. John Smith" className="w-full px-4 py-3 bg-slate-50 border border-border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/30 transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Business Name <span className="text-error">*</span></label>
                <input type="text" value={formData.business_name} onChange={e => setFormData({...formData, business_name: e.target.value})} placeholder="e.g. Smith Legal" className="w-full px-4 py-3 bg-slate-50 border border-border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/30 transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Website Domain <span className="text-error">*</span></label>
                <input type="text" value={formData.website_domain} onChange={e => setFormData({...formData, website_domain: e.target.value})} placeholder="e.g. smithlegal.com.au" className="w-full px-4 py-3 bg-slate-50 border border-border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/30 transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Plan</label>
                <select value={formData.plan} onChange={e => setFormData({...formData, plan: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/30 appearance-none">
                  <option>Standard</option><option>Premium</option><option>Enterprise</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-xl p-6 border border-border shadow-card">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-success/5 rounded-lg flex items-center justify-center text-success">
                  <SafeIcon icon={FiMail} />
                </div>
                <h3 className="text-lg font-semibold text-ink">Notification Settings</h3>
              </div>
              <p className="text-sm text-muted">Configure alert behavior for business and customers</p>
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-border">
                <div>
                  <p className="text-sm font-medium text-ink">Business Email Alerts</p>
                  <p className="text-xs text-muted">Instant notifications to partner inbox</p>
                </div>
                <input type="checkbox" checked={formData.send_business_notification} onChange={e => setFormData({...formData, send_business_notification: e.target.checked})} className="w-5 h-5 rounded border-slate-300 text-primary focus:ring-primary/20" />
              </div>
              
              {formData.send_business_notification && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Primary Email <span className="text-error">*</span></label>
                    <input type="email" value={formData.business_notification_email} onChange={e => setFormData({...formData, business_notification_email: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/30 transition-all" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">CC Email</label>
                    <input type="email" value={formData.cc_email} onChange={e => setFormData({...formData, cc_email: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/30 transition-all" />
                  </div>
                </div>
              )}
              
              <hr className="border-border" />
              
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-border">
                <div>
                  <p className="text-sm font-medium text-ink">Customer Auto-Reply</p>
                  <p className="text-xs text-muted">Instant acknowledgement to website visitors</p>
                </div>
                <input type="checkbox" checked={formData.send_customer_confirmation} onChange={e => setFormData({...formData, send_customer_confirmation: e.target.checked})} className="w-5 h-5 rounded border-slate-300 text-primary focus:ring-primary/20" />
              </div>

              {formData.send_customer_confirmation && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Reply-To Email <span className="text-error">*</span></label>
                    <input type="email" value={formData.reply_to_email} onChange={e => setFormData({...formData, reply_to_email: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/30 transition-all" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Sender Name</label>
                    <input type="text" value={formData.customer_ack_from_name} onChange={e => setFormData({...formData, customer_ack_from_name: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/30 transition-all" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-6 text-white relative overflow-hidden shadow-card">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl -mr-16 -mt-16"></div>
            <h3 className="text-base font-semibold mb-4 relative z-10">Configuration Status</h3>
            <div className="space-y-3 relative z-10">
              {[
                { label: 'Profile Complete', done: !!formData.business_name && !!formData.website_domain },
                { label: 'Saved', done: !!clientKey },
                { label: 'Test Lead Sent', done: !!testResult },
                { label: 'Portal Enabled', done: formData.client_portal_enabled && !!formData.client_login_emails }
              ].map((step, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all ${
                    step.done ? 'bg-success text-white' : 'border border-white/20 text-white/40'
                  }`}>
                    {step.done && <SafeIcon icon={FiCheck} className="text-xs" />}
                  </div>
                  <span className={`text-sm ${step.done ? 'text-white' : 'text-slate-400'}`}>{step.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-surface rounded-xl p-6 border border-border shadow-card">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-ink mb-1">Service Lifecycle</h3>
              <p className="text-sm text-muted">Manage subscription dates</p>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Start Date</label>
                <input type="date" value={formData.service_start_date} onChange={e => setFormData({...formData, service_start_date: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/30 transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">End Date</label>
                <input type="date" value={formData.service_end_date} onChange={e => setFormData({...formData, service_end_date: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/30 transition-all" />
                {formData.service_end_date && (
                  <div className="mt-2 p-2 bg-slate-50 rounded-md border border-border">
                    <p className="text-xs text-muted">Display: {formatDisplayDate(formData.service_end_date)}</p>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-border">
                <span className="text-sm font-medium text-slate-700">Status</span>
                <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="bg-white px-3 py-1.5 rounded-md text-xs font-medium outline-none border border-border">
                  <option>Trial</option><option>Active</option><option>Grace</option><option>Expired</option><option>Paused</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-xl p-6 border border-border shadow-card">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-ink mb-1">Client Portal</h3>
              <p className="text-sm text-muted">Access configuration</p>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-border">
                <div>
                  <p className="text-sm font-medium text-ink">Portal Enabled</p>
                  <p className="text-xs text-muted">Allow client access to leads</p>
                </div>
                <input type="checkbox" checked={formData.client_portal_enabled} onChange={e => setFormData({...formData, client_portal_enabled: e.target.checked})} className="w-5 h-5 rounded border-slate-300 text-primary focus:ring-primary/20" />
              </div>
              {formData.client_portal_enabled && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Authorized Emails <span className="text-error">*</span></label>
                    <input type="text" value={formData.client_login_emails} onChange={e => setFormData({...formData, client_login_emails: e.target.value})} placeholder="email@domain.com" className="w-full px-4 py-3 bg-slate-50 border border-border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/30 transition-all" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">6-Digit Passcode <span className="text-error">*</span></label>
                    <input type="text" value={formData.client_login_code} onChange={e => setFormData({...formData, client_login_code: e.target.value.replace(/\D/g, '').slice(0, 6)})} placeholder="••••••" className="w-full px-4 py-3 bg-white border border-primary/20 rounded-lg text-lg font-mono font-semibold tracking-widest outline-none shadow-sm" />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-surface rounded-xl p-6 border border-border shadow-card">
            <h3 className="text-base font-semibold text-ink mb-4">Integration Code</h3>
            <div className="bg-slate-50 border border-border rounded-lg p-4 min-h-[100px] flex items-center justify-center">
              {scriptInfo.code ? (
                <code className="text-sm text-primary font-mono leading-relaxed select-all font-medium">
                  {scriptInfo.code}
                </code>
              ) : (
                <p className="text-sm text-muted italic">Save configuration to generate script</p>
              )}
            </div>
            <button 
              type="button" 
              onClick={async () => {
                if (!scriptInfo.code) return setNotification({ message: 'Configuration not saved.', type: 'warning' });
                await navigator.clipboard.writeText(scriptInfo.code);
                setCopied(true);
                setNotification({ message: 'Copied to clipboard', type: 'success' });
                setTimeout(() => setCopied(false), 2000);
              }}
              className="w-full mt-4 py-3 bg-slate-900 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-slate-800 transition-all active:scale-95"
            >
              <SafeIcon icon={copied ? FiCheck : FiCopy} />
              <span>{copied ? 'Copied' : 'Copy Script'}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}